import {
  type AbstractTemplateInOut,
  type ConfigurationExpressionValueInOut,
  type ConfigurationFunctionValueInOut,
  type EnumeratedValueFrameInOut,
  type FunctionDescriptorOutput,
  isLibraryInOut,
  type RangedValueFrameInOut,
  type SimpleValueSetupInOut,
  type SubTemplateInOut,
  type SubTemplateValueSetupInOut,
  type TemplateInOut,
  type TemplatePropertyInOut,
  type TemplateValueSetupInOut,
  type ValidationInOut,
  type ValueFrameInOut,
  type ValueSetupInOut,
} from '@festina/configuration-general-types/configuration'
import {
  type LayerKey,
  resolveTopologicalPath,
  type Tree,
} from '@festina-life/api'
import {
  assertNotEmpty,
  type Except,
  notEmpty,
  type Optional,
} from '@festina-life/core'

import {
  functionDeclaration,
  functionIdentifier,
} from '../../components/templateFieldsTable/functionHelpers'
import { emptyMetaModel } from '../../context/metaModelHelpers'
import {
  isModelMetaType,
  type MetaModel,
  type MetaType,
  type ModelMetaType,
  type PropertyDescriptor,
} from '../../types/metaModel'
import {
  areVersionsEqual,
  type ConfigurationFieldExpressionValue,
  ConfigurationFieldType,
  type ConfigurationSimpleValueFieldValue,
  type ConfigurationTemplateFunctionValueCommonFields,
  type DeclaredConfigurationTemplateFunctionValue,
  type DynamicConfigurationTemplateFunctionValue,
  type EnumeratedValueFrameFieldValue,
  FrameType,
  isSubTemplatedField,
  MetaModelConfigurationTypeNameConfigurationFieldType,
  type RangedValueFrameFieldValue,
  type SimpleValueSetupFieldValue,
  type SubTemplateFieldValue,
  type SubTemplateValueSetupFieldValue,
  type SupportedEditableTemplateFieldValue,
  type TemplateField,
  type TemplateFunctions,
  type TemplateReferenceValueSetupFieldValue,
  type TemplateVersionInfo,
  type ValidationFieldValue,
  type ValueConfig,
  type ValueSetupFieldValue,
  ValueSetupRole,
} from '../../types/templateModel'
import {
  allPropertiesFromTemplateAndModel,
  enrichConfigurationField,
  toIndexedPath,
} from '../helpers'
import { getTemplateProperty } from '../templateOperations/helpers'
import {
  type Enriched,
  isTemplateInOut,
  type VersionedValue,
  type WithoutOpenApiType,
} from '../types'

export const subTemplateRole = (
  modelType: Optional<ModelMetaType>,
): ValueSetupRole => {
  if (modelType?.stereoTypes.find((e) => e === 'IdentifiableSubTemplated')) {
    return ValueSetupRole.IDENTIFIABLE_SUB_TEMPLATE
  }

  if (!!modelType && modelType.subTypes.length === 0) {
    return ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE
  }

  return ValueSetupRole.SUB_CLASSED_SUB_TEMPLATE
}

export const generateFunctions = ({
  parent,
  updateField,
  path,
  meta,
  isParentNotRelevant,
}: {
  path: string
  parent: Enriched<AbstractTemplateInOut>
  updateField: (nextValue: TemplateFunctions) => void
  meta: Optional<MetaType>
  isParentNotRelevant: boolean
}): TemplateFunctions => {
  if (!parent) {
    return { dynamicFunctions: null, declaredFunctions: null }
  }

  const allFunctions = {
    dynamicFunctions: generateDynamicFunctionsArrayField({
      parent,
      path,
      isParentNotRelevant,
      updateField: (nextDynamicFunctions) => {
        updateField({ ...allFunctions, dynamicFunctions: nextDynamicFunctions })
      },
    }),
    declaredFunctions: generateDeclaredFunctionsArray({
      parent,
      meta,
      isParentNotRelevant,
      updateField: (nextDeclaredFunctions) => {
        updateField({
          ...allFunctions,
          declaredFunctions: nextDeclaredFunctions,
        })
      },
      path,
    }),
  }

  return allFunctions
}

type GenerateDeclaredFunctionsParams = {
  path: string
  parent: Enriched<AbstractTemplateInOut>
  updateField: (
    nextFunctions: DeclaredConfigurationTemplateFunctionValue[],
  ) => void
  meta: Optional<MetaType>
  isParentNotRelevant: boolean
}

const generateDeclaredFunctionsArray = ({
  parent,
  meta,
  updateField,
  path,
  isParentNotRelevant,
}: GenerateDeclaredFunctionsParams) => {
  const declaredFunctionDescriptors =
    (isModelMetaType(meta) &&
      meta.configurationDetails?.functions.filter(
        (functionDescriptor) => !functionDescriptor.internal,
      )) ||
    []

  const declaredFunctionsOnTemplate =
    parent.declaredFunctions?.filter(notEmpty) ?? []
  const allModeledFunctionSignatures = declaredFunctionDescriptors
    .map((descriptor) => functionDeclaration(descriptor.signature))
    .filter(notEmpty)

  const nonModeledDeclaredFunctions = declaredFunctionsOnTemplate
    .filter(
      (declaredFunction) =>
        !allModeledFunctionSignatures.includes(
          functionDeclaration(declaredFunction.function) ?? '',
        ),
    )
    .map((nonModeledFunction) => {
      assertNotEmpty(nonModeledFunction.origin)

      return generateDeclaredConfigurationTemplateFunctionValue({
        value: nonModeledFunction,
        path: functionPath({
          basePath: path,
          source: nonModeledFunction.function ?? '',
        }),
        descriptor: null,
        isParentNotRelevant,
      })
    })

  const nonDeclaredFunctionIdentifiers: string[] = []
  const allDeclaredFunctionsFromModel = declaredFunctionDescriptors.map(
    (declaredFunctionDescriptor) => {
      const declaredFunction = declaredFunctionsOnTemplate?.find(
        (templateFunction) => {
          const typedFunctionSignatureWithoutSpaces = functionDeclaration(
            templateFunction.function,
          )
          const typedDescriptorSignatureWithoutSpaces = functionDeclaration(
            declaredFunctionDescriptor.signature,
          )

          return (
            typedFunctionSignatureWithoutSpaces ===
            typedDescriptorSignatureWithoutSpaces
          )
        },
      )

      if (!declaredFunction && declaredFunctionDescriptor.signature) {
        nonDeclaredFunctionIdentifiers.push(
          functionIdentifier({
            descriptor: declaredFunctionDescriptor,
            source: undefined,
          }),
        )
      }

      return generateDeclaredConfigurationTemplateFunctionValue({
        value: declaredFunction,
        path: functionPath({
          basePath: path,
          source:
            declaredFunction?.function ?? declaredFunctionDescriptor.signature,
        }),
        descriptor: declaredFunctionDescriptor,
        isParentNotRelevant,
      })
    },
  )

  const allDeclaredFunctions = [
    ...nonModeledDeclaredFunctions,
    ...allDeclaredFunctionsFromModel,
  ].map(
    (declaredFunction, index) =>
      ({
        ...declaredFunction,
        updateRaw: (nextValue: ConfigurationFunctionValueInOut) => {
          const declaredFunctionPath = functionPath({
            basePath: path,
            source: nextValue.function ?? '',
          })
          const enrichedFunctionValue = enrichConfigurationField({
            value: nextValue,
            metaModel: emptyMetaModel,
            path: declaredFunctionPath,
          })
          const visualFunctionValue =
            generateDeclaredConfigurationTemplateFunctionValue({
              descriptor: declaredFunction.descriptor,
              value: enrichedFunctionValue,
              path: enrichedFunctionValue.origin ?? path,
              isParentNotRelevant: false,
            })

          const nextFunctions = [...allDeclaredFunctions]
          nextFunctions.splice(
            index,
            1,
            visualFunctionValue as DeclaredConfigurationTemplateFunctionValue,
          )
          updateField(nextFunctions)
        },
        update: (nextFunctionValue) => {
          const nextFunctions = [...allDeclaredFunctions]

          if (nextFunctionValue) {
            const copiedFunctionValue = {
              ...nextFunctionValue,
              origin: nextFunctionValue.path,
            }
            nextFunctions.splice(index, 1, copiedFunctionValue)
          } else {
            nextFunctions.splice(index, 1)
          }

          const updatedFunctionIdentifier =
            nextFunctionValue?.functionIdentifier

          updateField(
            nextFunctions.filter(
              (nextFunctionCandidate) =>
                nextFunctionCandidate.functionIdentifier ===
                  updatedFunctionIdentifier ||
                !nonDeclaredFunctionIdentifiers.includes(
                  nextFunctionCandidate.functionIdentifier,
                ),
            ),
          )
        },
      }) satisfies DeclaredConfigurationTemplateFunctionValue,
  )

  return allDeclaredFunctions
}

type GenerateDynamicFunctionsArrayFieldInput<T extends AbstractTemplateInOut> =
  {
    path: string
    isParentNotRelevant: boolean
    parent: Enriched<T>
    updateField: (
      nextFunctions: DynamicConfigurationTemplateFunctionValue[],
    ) => void
  }

const generateDynamicFunctionsArrayField = <T extends AbstractTemplateInOut>({
  parent,
  path,
  updateField,
  isParentNotRelevant,
}: GenerateDynamicFunctionsArrayFieldInput<T>): Optional<
  DynamicConfigurationTemplateFunctionValue[]
> => {
  const functions = parent.dynamicFunctions

  if (!functions) {
    return null
  }

  const allFunctions = functions.filter(notEmpty).map((func, index) => {
    assertNotEmpty(func.origin)

    const templateFunctionValue =
      generateDynamicConfigurationTemplateFunctionValue({
        value: func,
        path: functionPath({ basePath: path, source: func.function }),
        isParentNotRelevant,
      })

    return {
      ...templateFunctionValue,
      isParentNotRelevant,
      updateRaw: (nextValue: ConfigurationFunctionValueInOut) => {
        const dynamicFunctionPath = functionPath({
          basePath: path,
          source: nextValue.function ?? '',
        })
        const enrichedFunctionValue = enrichConfigurationField({
          value: nextValue,
          metaModel: emptyMetaModel,
          path: dynamicFunctionPath,
        })
        const visualFunctionValue =
          generateDynamicConfigurationTemplateFunctionValue({
            value: enrichedFunctionValue,
            path: dynamicFunctionPath,
          })

        const nextFunctions = [...allFunctions]
        nextFunctions.splice(
          index,
          1,
          visualFunctionValue as DynamicConfigurationTemplateFunctionValue,
        )
        updateField(nextFunctions)
      },
      update: (nextFunctionValue) => {
        const nextFunctions = [...allFunctions]
        if (nextFunctionValue) {
          const copiedFunctionValue = {
            ...nextFunctionValue,
            origin: nextFunctionValue.path,
          }
          nextFunctions.splice(index, 1, copiedFunctionValue)
        } else {
          nextFunctions.splice(index, 1)
        }

        updateField(nextFunctions)
      },
    } satisfies DynamicConfigurationTemplateFunctionValue
  })

  return allFunctions
}

type TemplateFieldsCreatorInput<T extends TemplateInOut | SubTemplateInOut> = {
  template?: Optional<Enriched<T>>
  path: string
  meta: MetaModel
  templateType: Optional<ModelMetaType>
  updateField: (nexFields: TemplateField[]) => Promise<void>
  isParentNotRelevant: boolean
}

export const generateFields = <T extends AbstractTemplateInOut>({
  template,
  path,
  meta,
  templateType,
  updateField,
  isParentNotRelevant,
}: TemplateFieldsCreatorInput<T>): TemplateField[] => {
  const typedTemplate = template as Optional<T>
  const allProperties = allPropertiesFromTemplateAndModel(
    typedTemplate,
    templateType,
  )

  const uiModel: TemplateField[] = Array.from(allProperties.entries())
    .map(([name, descriptor]) => {
      let propertyInOut: Optional<Enriched<TemplatePropertyInOut>>

      if (
        name === 'valid' &&
        typedTemplate &&
        (isLibraryInOut(typedTemplate) || isTemplateInOut(typedTemplate))
      ) {
        const validField = typedTemplate.valid
        const origin =
          (validField as Optional<Enriched<ConfigurationExpressionValueInOut>>)
            ?.origin ?? `{${typedTemplate.name}}.valid`

        propertyInOut = validField
          ? {
              _type: 'TemplatePropertyInOut',
              name: 'valid',
              origin,
              value: enrichConfigurationField({
                value: validField,
                path: origin,
                metaModel: meta,
              }),
            }
          : undefined
      } else {
        propertyInOut = template
          ? (getTemplateProperty(template as T, name) as Optional<
              Enriched<TemplatePropertyInOut>
            >)
          : undefined
      }

      return { descriptor, propertyInOut }
    })
    .filter(({ descriptor, propertyInOut }) => !!descriptor || !!propertyInOut)
    .map(({ descriptor, propertyInOut }, index) => {
      if (propertyInOut?.value) {
        assertNotEmpty(propertyInOut?.value.origin)
      }

      const fieldPath = `${path}.${descriptor?.name ?? propertyInOut?.name}`

      const generatedField = generateField({
        field: {
          descriptor,
          propertyInOut,
        },
        path: fieldPath,
        meta,
        templateType,
        isParentNotRelevant,
        updateField: (nextValue) => {
          const nextFields = [...uiModel]
          nextFields.splice(index, 1, {
            ...generatedField,
            origin: generatedField.path,
            value: nextValue,
          } as TemplateField)

          return updateField(nextFields)
        },
      })

      return generatedField
    })

  return uiModel
}

type MetaInformation = {
  metaModel: MetaModel
  currentTemplateType: Optional<ModelMetaType>
}

type TemplateFieldCreatorInput<T extends SupportedEditableTemplateFieldValue> =
  {
    field: {
      descriptor: Optional<PropertyDescriptor>
      propertyInOut: Optional<Enriched<TemplatePropertyInOut>>
    }
    path: string
    meta: MetaModel
    templateType: Optional<ModelMetaType>
    updateField: (nextValue: T) => Promise<void>
    isParentNotRelevant: boolean
  }

const valueGenerator = <T extends SupportedEditableTemplateFieldValue>({
  path,
  meta,
  templateType,
  originalPropertyType,
  updateField,
  isParentNotRelevant,
}: {
  propertyDescriptor: Optional<PropertyDescriptor>
  path: string
  meta: MetaModel
  templateType: Optional<ModelMetaType>
  originalPropertyType: Optional<ModelMetaType>
  updateField: (nextValue: T) => Promise<void>
  isParentNotRelevant: boolean
}) => {
  const valueMetadata = {
    metaModel: meta,
    currentTemplateType: templateType,
  }

  return {
    [ConfigurationFieldType.SimpleValueSetup]: (
      value: Enriched<SimpleValueSetupInOut>,
      path: string,
    ) =>
      generateVisualSimpleValueSetup({
        value,
        path,
        updateField: updateField as (
          nextValue: SimpleValueSetupFieldValue,
        ) => Promise<void>,
      }),
    [ConfigurationFieldType.SubTemplateValueSetup]: (
      value: Enriched<SubTemplateValueSetupInOut>,
    ) =>
      generateSubTemplateValueSetup({
        value,
        path,
        meta: valueMetadata,
        originalFieldType: originalPropertyType,
        isParentNotRelevant,
        updateField: updateField as (
          nextValue: SubTemplateValueSetupFieldValue,
        ) => Promise<void>,
      }),
    [ConfigurationFieldType.TemplateValueSetup]: generateTemplateValueSetup,
    [ConfigurationFieldType.ConfigurationExpressionValue]: (
      value: Enriched<ConfigurationExpressionValueInOut>,
      path: string,
    ) =>
      gcsv({
        value,
        basePath: path,
      }) as unknown as ConfigurationSimpleValueFieldValue,
  }
}

export const ValueInOutToConfigurationFieldTypeMapper = {
  SimpleValueSetupInOut: ConfigurationFieldType.SimpleValueSetup,
  SubTemplateValueSetupInOut: ConfigurationFieldType.SubTemplateValueSetup,
  TemplateValueSetupInOut: ConfigurationFieldType.TemplateValueSetup,
  ConfigurationExpressionValueInOut:
    ConfigurationFieldType.ConfigurationExpressionValue,
}

const generateField = <T extends SupportedEditableTemplateFieldValue>({
  field,
  path,
  meta,
  templateType,
  updateField,
  isParentNotRelevant,
}: TemplateFieldCreatorInput<T>): TemplateField => {
  const fieldType = field.descriptor?.typeName
    ? MetaModelConfigurationTypeNameConfigurationFieldType[
        field.descriptor.typeName
      ]
    : ValueInOutToConfigurationFieldTypeMapper[
        field.propertyInOut?.value
          ?._type as keyof typeof ValueInOutToConfigurationFieldTypeMapper
      ]
  const fieldName = field.descriptor?.name ?? field.propertyInOut?.name

  assertNotEmpty(fieldType)
  assertNotEmpty(fieldName)

  const abstractTypeFields = {
    origin: field.propertyInOut?.origin,
    name: fieldName,
    role: generateRole(fieldType, field.descriptor?.onlyTemplatedType),
    parentTemplateType: templateType,
    templatePropertyDescriptor: field.descriptor,
    originalPropertyType: field.descriptor?.onlyTemplatedType,
    modelPropertyDescriptor: templateType?.modelType?.properties?.find(
      (p) => p.name === fieldName,
    ),
    path,
    isParentNotRelevant,
  }
  const commonValueProps = {
    documentation: field.propertyInOut?.value?.documentation,
  }

  const generator = valueGenerator({
    propertyDescriptor: field.descriptor,
    path,
    meta,
    templateType,
    originalPropertyType:
      abstractTypeFields.originalPropertyType as Optional<ModelMetaType>,
    updateField,
    isParentNotRelevant,
  })

  const commonFields = {
    ...abstractTypeFields,
    value: field.propertyInOut?.value
      ? {
          ...generator[fieldType](field.propertyInOut?.value, path),
          ...commonValueProps,
        }
      : null,
    fieldType,
  }

  const generatedField = {
    ...commonFields,
    update: (updatedVisualValue: T) => updateField(updatedVisualValue),
    updateRaw: (rawValue) => {
      const { _type, ...values } = enrichConfigurationField({
        value: rawValue,
        metaModel: meta,
        path,
      })

      assertNotEmpty(_type)

      const convertedRawValue = generator[fieldType](
        { ...values, _type },
        path,
      ) as T

      return updateField(convertedRawValue)
    },
  } as TemplateField

  if (isSubTemplatedField(generatedField)) {
    generatedField.value?.variants.forEach((variant) => {
      variant.subTemplatedField = generatedField
    })
  }

  return generatedField
}

const generateRole = (
  configurationFieldType: ConfigurationFieldType,
  originalType: Optional<MetaType>,
): ValueSetupRole => {
  if (configurationFieldType === ConfigurationFieldType.TemplateValueSetup) {
    return ValueSetupRole.REFERENCE
  } else if (
    configurationFieldType === ConfigurationFieldType.SubTemplateValueSetup
  ) {
    return subTemplateRole(originalType as Optional<ModelMetaType>)
  }

  return ValueSetupRole.SIMPLE
}

const generateCommonValueSetupFields = (
  value: Enriched<ValueSetupInOut>,
  path: string,
): ValueSetupFieldValue => {
  assertNotEmpty(value.origin)

  return {
    path,
    ...(value.documentation
      ? { documentation: value.documentation as VersionedValue<string> }
      : {}),
    required: gcsv({
      value: value.required,
      basePath: path,
      fieldName: 'required',
    }),
    requiredInModelInstance: gcsv({
      value: value.requiredInModelInstance,
      basePath: path,
      fieldName: 'requiredInModelInstance',
    }),
    ...(value.valueConfig
      ? { valueConfig: value.valueConfig as VersionedValue<ValueConfig> }
      : {}),
    validations: generateValidations(
      value.validations as Enriched<ValidationInOut>[] | null,
      path,
    ),
    fieldType: ConfigurationFieldType.SimpleValueSetup,
    origin: value.origin,
  }
}

const generateValidations: (
  validations: Enriched<ValidationInOut>[] | null,
  basePath: string,
) => ValidationFieldValue[] = (validations, basePath) =>
  !validations
    ? []
    : validations.map(({ origin, ...validationValue }, index) => {
        const path = toIndexedPath({
          basePath,
          propName: 'validations',
          index,
        })
        assertNotEmpty(origin)

        return {
          validation: gcsv({
            value: validationValue.validation,
            basePath: path,
            fieldName: 'validation',
          }),
          path,
          key: gcsv({
            value: validationValue.key,
            basePath: path,
            fieldName: 'key',
          }),
          defaultPattern: gcsv({
            value: validationValue.defaultPattern,
            basePath: path,
            fieldName: 'defaultPattern',
          }),
          keyArguments: gcsv({
            value: validationValue.keyArguments,
            basePath: path,
            fieldName: 'keyArguments',
          }),
          origin,
        }
      })

const generateVisualSimpleValueSetup = ({
  value,
  path,
  updateField,
}: {
  value: Enriched<SimpleValueSetupInOut>
  path: string
  updateField: (nextValue: SimpleValueSetupFieldValue) => void
}): WithoutOpenApiType<SimpleValueSetupFieldValue> => {
  const visualValue = {
    ...generateCommonValueSetupFields(value, path),
    role: ValueSetupRole.SIMPLE,
    defaultValue: gcsv({
      value: value.defaultValue,
      basePath: path,
      fieldName: 'defaultValue',
    }),
    frame: generateFrame(value.frame, `${path}.frame`),
    dynamicFunctions: generateDynamicFunctionsArrayField({
      path,
      parent: value,
      updateField: (nextFunctions) => {
        updateField({ ...visualValue, dynamicFunctions: nextFunctions })
      },
      isParentNotRelevant: false,
    }),
  }

  return visualValue
}

const ValueFrameInOutToFrameType = {
  EnumeratedValueFrameInOut: FrameType.EnumeratedValueFrame,
  RangedValueFrameInOut: FrameType.RangedValueFrame,
} as const

const generateFrame = (
  frame: Enriched<ValueFrameInOut> | null | undefined,
  path: string,
): Optional<EnumeratedValueFrameFieldValue> | RangedValueFrameFieldValue => {
  if (!frame) {
    return null
  }

  const type =
    ValueFrameInOutToFrameType[
      frame._type as keyof typeof ValueFrameInOutToFrameType
    ]

  return {
    [FrameType.EnumeratedValueFrame]: generateEnumeratedFrame(frame, path),
    [FrameType.RangedValueFrame]: generateRangedFrame(frame, path),
  }[type]
}

const generateEnumeratedFrame = (
  frame: Enriched<EnumeratedValueFrameInOut>,
  path: string,
): EnumeratedValueFrameFieldValue => ({
  type: FrameType.EnumeratedValueFrame,
  entries: gcsv({
    value: frame.entries,
    basePath: path,
    fieldName: 'entries',
  }),
  origin: frame.entries?.origin?.replace(/\.entries$/, ''),
})

const generateRangedFrame = (
  frame: Enriched<RangedValueFrameInOut>,
  path: string,
): RangedValueFrameFieldValue => ({
  type: FrameType.RangedValueFrame,
  range: gcsv({ value: frame.range, basePath: path, fieldName: 'range' }),
  origin: frame.range?.origin?.replace(/\.range$/, ''),
})

const generateSubTemplateValueSetup = ({
  value,
  path,
  meta,
  originalFieldType,
  updateField,
  isParentNotRelevant,
}: {
  value: Enriched<SubTemplateValueSetupInOut>
  path: string
  meta: MetaInformation
  originalFieldType: Optional<ModelMetaType>
  isParentNotRelevant: boolean
  updateField: (nextValue: SubTemplateValueSetupFieldValue) => Promise<void>
}): SubTemplateValueSetupFieldValue => {
  const nonVariantFields = {
    ...generateCommonValueSetupFields(value, path),
    availableVariantTypes: originalFieldType
      ? [...(originalFieldType.subTypes as ModelMetaType[]), originalFieldType]
      : [],
    cardinality: gcsv({
      value: value.cardinality,
      basePath: path,
      fieldName: 'cardinality',
    }),
    fieldType: ConfigurationFieldType.SubTemplateValueSetup,
  }

  const variants = generateVariants({
    variants: value.variants as Enriched<SubTemplateInOut>[] | null | undefined,
    path,
    meta,
    isParentNotRelevant:
      nonVariantFields.valueConfig?.value === 'notRelevant' ||
      isParentNotRelevant,
    updateVariants: (nextVariants) =>
      updateField({
        ...nonVariantFields,
        variants: nextVariants,
      }),
  })

  return {
    ...nonVariantFields,
    variants,
  }
}

const generateVariants = ({
  variants,
  path,
  meta,
  updateVariants,
  isParentNotRelevant,
}: {
  variants: Optional<Enriched<SubTemplateInOut>[]>
  path: string
  meta: MetaInformation
  isParentNotRelevant: boolean
  updateVariants: (nextVariants: SubTemplateFieldValue[]) => Promise<void>
}): SubTemplateFieldValue[] => {
  if (!variants) {
    return []
  }

  const currentVariants: SubTemplateFieldValue[] = variants.map(
    (variant, variantPosition) => {
      assertNotEmpty(variant.templateTypeName)

      const type = meta.metaModel.types.get(variant.templateTypeName)
      const subTemplateIdentifier = variant.name
      assertNotEmpty(subTemplateIdentifier)

      const variantPath = `${path}{${subTemplateIdentifier}}`
      const fields = generateFields({
        template: variant,
        path: variantPath,
        meta: meta.metaModel,
        templateType: isModelMetaType(type) ? type : undefined,
        isParentNotRelevant,
        updateField: (nextFields) => {
          const nextVariant = {
            ...currentVariants[variantPosition],
            origin: variantPath,
            fields: nextFields,
          } satisfies SubTemplateFieldValue

          const nextVariants = [...currentVariants]
          nextVariants.splice(variantPosition, 1, nextVariant)
          return updateVariants(nextVariants)
        },
      })
      const functions = generateFunctions({
        parent: variant,
        path: `${path}{${subTemplateIdentifier}}`,
        meta: meta.metaModel.types.get(
          variant.templateTypeName,
        ) as ModelMetaType,
        isParentNotRelevant,
        updateField: (nextValue) => {
          const nextVariant = {
            ...currentVariants[variantPosition],
            declaredFunctions: nextValue.declaredFunctions,
            dynamicFunctions: nextValue.dynamicFunctions,
            origin: null,
          } as SubTemplateFieldValue

          const nextVariants = [...currentVariants]
          nextVariants.splice(variantPosition, 1, nextVariant)
          return updateVariants(nextVariants)
        },
      })

      const basePath = `${path}{${subTemplateIdentifier}}`
      const modelTypeName = variant.modelTypeName ?? ''
      const modelType = meta.metaModel.types.get(modelTypeName)

      return {
        fields: fields.filter(
          (t) => t.name !== 'defaultSelected' && t.name !== 'selectable',
        ),
        defaultSelected: gcsv({
          value: variant.defaultSelected,
          basePath,
          fieldName: 'defaultSelected',
        }),
        selectable: gcsv({
          value: variant?.selectable,
          basePath,
          fieldName: 'selectable',
        }),
        path: basePath,
        declaredFunctions: functions.declaredFunctions,
        dynamicFunctions: functions.dynamicFunctions,
        ...(variant.parent
          ? { parent: variant.parent as VersionedValue<string> }
          : {}),
        type: isModelMetaType(type) ? type : undefined,
        typeName: variant.templateTypeName,
        subTemplateIdentifier,
        modelType: isModelMetaType(modelType) ? modelType : undefined,
        modelTypeName,
        ...(variant.documentation
          ? { documentation: variant.documentation as VersionedValue<string> }
          : {}),
        origin: variant.origin,
      } satisfies SubTemplateFieldValue
    },
  )

  return currentVariants
}

const generateTemplateValueSetup = (
  value: Enriched<TemplateValueSetupInOut>,
  path: string,
): TemplateReferenceValueSetupFieldValue => ({
  ...generateCommonValueSetupFields(value, path),
  cardinality: gcsv({
    value: value.cardinality,
    basePath: path,
    fieldName: 'cardinality',
  }),
  variants: !value.variants
    ? []
    : value.variants?.map((v, index) => {
        assertNotEmpty(v?.origin)

        const newPath = toIndexedPath({
          basePath: path,
          propName: 'variants',
          index,
        })

        return {
          path: newPath,
          defaultSelected: gcsv({
            value: v?.defaultSelected,
            basePath: newPath,
            fieldName: 'defaultSelected',
          }),
          selectable: gcsv({
            value: v?.selectable,
            basePath: newPath,
            fieldName: 'selectable',
          }),
          cardinality: gcsv({
            value: v?.cardinality,
            basePath: newPath,
            fieldName: 'cardinality',
          }),
          source: v?.name as string,
          key: v?.key,
          origin: v.origin,
        }
      }),
})

const gcsv = ({
  value,
  basePath,
  fieldName,
}: {
  value: Optional<Enriched<ConfigurationExpressionValueInOut>>
  basePath: string
  fieldName?: string
}): Optional<ConfigurationFieldExpressionValue> => {
  if (!value) {
    return value
  }

  assertNotEmpty(value.origin)

  return {
    fieldType: ConfigurationFieldType.ConfigurationExpressionValue,
    path: fieldName ? `${basePath}.${fieldName}` : basePath, //if the fieldName is null, we are directly on the expression already
    ...(value.documentation
      ? { documentation: value.documentation as VersionedValue<string> }
      : {}),
    origin: value.origin,
    expression: {
      fplusSource: value.expression,
    },
  }
}

export const generateDeclaredConfigurationTemplateFunctionValue = ({
  value,
  path,
  descriptor,
  isParentNotRelevant,
}: {
  value?: Enriched<ConfigurationFunctionValueInOut>
  path: string
  descriptor: Optional<FunctionDescriptorOutput>
  isParentNotRelevant: boolean
}): Except<
  DeclaredConfigurationTemplateFunctionValue,
  'update' | 'updateRaw'
> => {
  const identifier = functionIdentifier({
    descriptor,
    source: value?.function,
  })

  return {
    ...generateCommonConfigurationTemplateFunctionValues(value),
    isParentNotRelevant,
    path,
    type: 'declared',
    functionIdentifier: identifier,
    descriptor,
  }
}

const generateCommonConfigurationTemplateFunctionValues = (
  value?: Enriched<ConfigurationFunctionValueInOut>,
): Partial<ConfigurationTemplateFunctionValueCommonFields> => ({
  ...(value?.documentation
    ? { documentation: value.documentation as VersionedValue<string> }
    : {}),
  function: {
    fplusSource: value?.function,
  },
  origin: value?.origin,
})

export const functionPath = ({
  basePath,
  source,
}: {
  basePath: string
  source: Optional<string>
}) => {
  const identifier = functionIdentifier({
    source,
  })

  return `${basePath}.${identifier}`
}

export const generateDynamicConfigurationTemplateFunctionValue = ({
  value,
  path,
  isParentNotRelevant,
}: {
  value: Enriched<ConfigurationFunctionValueInOut>
  path: string
  isParentNotRelevant?: boolean
}): Except<
  DynamicConfigurationTemplateFunctionValue,
  'update' | 'updateRaw'
> => {
  const identifier = functionIdentifier({
    source: value?.function,
  })

  return {
    ...generateCommonConfigurationTemplateFunctionValues(value),
    path,
    type: 'dynamic',
    functionIdentifier: identifier,
    isParentNotRelevant: isParentNotRelevant ?? false,
  }
}

const finalVersionWithReplacements = ({
  versionInfo,
  visitedOperationKeys,
  layer,
  generalLayers,
}: {
  versionInfo: TemplateVersionInfo
  visitedOperationKeys: string[]
  layer: LayerKey
  generalLayers: LayerKey[]
}): TemplateVersionInfo[] => {
  if (!versionInfo.replacedBy?.length) {
    return versionInfo.layer.equals(layer) ||
      generalLayers.some((generalLayer) =>
        generalLayer.equals(versionInfo.layer),
      )
      ? [versionInfo]
      : []
  }

  return versionInfo.replacedBy.flatMap((replacedByVersion) =>
    finalVersionWithReplacements({
      versionInfo: replacedByVersion,
      visitedOperationKeys: [
        ...visitedOperationKeys,
        versionInfo.operationKey ?? '',
      ],
      layer,
      generalLayers,
    }),
  )
}

/**
 * Given a template identifier function will return the template hierarchy chain as an ordered list of templates.
 * The topmost parent will be first element, and the input template will be the last element in the list.
 */
export const getTemplateHierarchy = ({
  templateInfo,
  shouldProcessReplacements,
  layerTopology,
}: {
  templateInfo: TemplateVersionInfo
  shouldProcessReplacements: boolean
  layerTopology: Optional<Tree>
}): TemplateVersionInfo[] => {
  const templateParent = templateInfo.parent
  const isParentReplacedByCurrentTemplate =
    templateParent?.replacedByIds?.includes(templateInfo.identifier) &&
    templateParent.replacedBy?.some(
      (replacedByVersion) =>
        replacedByVersion.operationKey === templateInfo.operationKey,
    )
  const topologicalPath = resolveTopologicalPath(
    templateInfo.layer,
    layerTopology,
  )
  const realParent =
    templateParent &&
    !isParentReplacedByCurrentTemplate &&
    shouldProcessReplacements
      ? (finalVersionWithReplacements({
          versionInfo: templateParent,
          visitedOperationKeys: [],
          layer: templateInfo.layer,
          generalLayers: topologicalPath.slice(0, -1),
        })
          .filter(notEmpty)
          .at(0) ?? templateParent)
      : templateParent

  return realParent && !areVersionsEqual(realParent, templateInfo)
    ? [
        ...getTemplateHierarchy({
          templateInfo: realParent,
          shouldProcessReplacements,
          layerTopology,
        }),
        templateInfo,
      ]
    : [templateInfo]
}
